/**
 * What became of a plan the owner approved, said in the conversation he
 * approved it in.
 *
 * Approving used to be the end of the thread: the work went out, and the only
 * place it came back was the Monitor Center. So the owner approved, and then
 * had to go somewhere else to find out whether it worked. This closes that
 * loop - the turn that carried the decision goes on to carry the result.
 *
 * The link from a plan to its runs is EXACT, not guessed. Approving a plan
 * creates a mission and writes the plan's tasks into it, and the plan keeps the
 * mission's id; a finished run names the task it ran. Plan -> mission -> tasks
 * -> runs, with no matching on titles, no matching on time, and no guessing.
 * When a link in that chain is missing - an older plan with no mission, a
 * mission whose tasks are not in the rows the browser holds - this says it does
 * not know yet rather than attaching the plan to a run that may not be its own.
 *
 * Pure: rows in, words out, and the caller passes the clock it wants.
 */
import type { AuditEvent, FinishedRun, MissionTask, Plan, RunningTask, WaitingJobRecoveryRow, Worker } from './data';
import { gatePhrase, laneEligibilityFact, reasonPhrase } from './audit';
import { durationPhrase, runDurationMs, toolPhrase, workerPhrase } from './finished';
import { plainTerminalText } from './terminal-plain-text';

/** Where one task of an approved plan has got to. */
export type TaskState = 'succeeded' | 'failed' | 'running' | 'waiting';

/** One task of an approved plan, and what came of it. */
export interface TaskOutcome {
  readonly taskId: string;
  readonly title: string;
  readonly state: TaskState;
  /** The one line under the title: how it went, who held it, how long it took. */
  readonly line: string;
  /** What the run itself said, when it said anything. */
  readonly detail: string | null;
  /**
   * Waiting only for capacity: the Workers that can do it, or the one the owner named, are
   * busy. Such a task is waiting to start, not blocked.
   */
  readonly capacityWait?: boolean;
}

/** What became of an approved plan, as far as the rows on the screen can say. */
export interface PlanOutcome {
  /** The one line the owner reads without opening anything. */
  readonly headline: string;
  /** True once nothing of this plan is still going or still waiting to start. */
  readonly settled: boolean;
  /** True when any task of it failed. */
  readonly failed: boolean;
  /** Empty when the plan's runs cannot be identified - never filled with a guess. */
  readonly tasks: readonly TaskOutcome[];
}

/**
 * Said for an approved plan whose runs cannot be identified from the rows in
 * hand. It is deliberately about what is known rather than about a failure:
 * nothing has come back HERE, which is not the same as nothing having happened.
 */
export const OUTCOME_NOT_MATCHED = 'Approved. Nothing has reported back on this plan yet.';

/** Said for a task that has been written but that no run has picked up. */
export const TASK_WAITING = 'Waiting to be picked up.';

/** Said when Brunel closed a waiting job as done elsewhere and recorded no line of its own. */
export const TASK_DONE_ELSEWHERE = 'Done elsewhere: its work had already landed.';

/** Said when a waiting job expired and no line of its own was recorded. */
export const TASK_EXPIRED = 'Expired: no Worker could take it for 24 hours.';

/**
 * A job Brunel closed without running it: its work had already landed (done), or nothing
 * could take it for a day (not done). Only when the task's own status agrees with the record;
 * a job that was handed to another Worker is still an ordinary waiting or running job.
 */
function closedWithoutRun(task: MissionTask, recoveries: readonly WaitingJobRecoveryRow[]): TaskOutcome | null {
  const row = recoveries.find((item) => item.task_id === task.id && item.project_id === task.project_id);
  if (row === undefined) return null;
  const line = (row.reason ?? '').trim();
  if (row.outcome === 'done_elsewhere' && task.status === 'completed') {
    return { taskId: task.id, title: task.title, state: 'succeeded', line: line === '' ? TASK_DONE_ELSEWHERE : line, detail: null };
  }
  if (row.outcome === 'expired' && task.status === 'discarded') {
    return { taskId: task.id, title: task.title, state: 'failed', line: line === '' ? TASK_EXPIRED : line, detail: null };
  }
  return null;
}

/** The task's newest dispatch decision: accepted, denied, or still waiting and why. */
function latestDispatchEvent(taskId: string, audit: readonly AuditEvent[]): AuditEvent | undefined {
  return audit
    .filter((event) => (event.event_type === 'dispatch.denied' || event.event_type === 'dispatch.accepted'
      || event.event_type === 'dispatch.waiting')
      && event.payload?.taskId === taskId)
    .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))[0];
}

function dispatchBlockerLine(
  taskId: string,
  audit: readonly AuditEvent[],
  workers: readonly Worker[],
): string | null {
  const latest = latestDispatchEvent(taskId, audit);
  if (latest === undefined || latest.event_type === 'dispatch.accepted') return null;

  const workerId = typeof latest.payload?.workerId === 'string' ? latest.payload.workerId : null;
  const worker = workerId === null ? undefined : workers.find((row) => row.id === workerId);
  const named = typeof latest.payload?.workerNumber === 'number' ? `Worker ${latest.payload.workerNumber}` : null;
  const who = worker === undefined ? named ?? 'the intended Worker' : `Worker ${worker.number}`;
  if (latest.event_type === 'dispatch.waiting') {
    const line = waitingLine(latest.reason_code, who, latest.payload?.requiredCapabilities);
    const placement = laneEligibilityFact(latest.payload);
    return placement === null ? line : `${line} ${placement.label}: ${placement.value}.`;
  }
  switch (latest.reason_code) {
    case 'node_unreachable':
      return `Could not dispatch to ${who}: its computer is offline or stopped reporting.`;
    case 'pinned_runtime_unavailable':
      return `Could not dispatch to ${who}: its pinned AI runtime is unavailable. Brunel did not substitute another runtime.`;
    case 'pinned_offering_unavailable':
      return `Could not dispatch to ${who}: its pinned AI offering is not available on that computer. Brunel did not substitute another offering.`;
    case 'no_eligible_session':
      return `Could not dispatch to ${who}: it has no eligible session for this project and workspace.`;
    default: {
      const why = reasonPhrase(latest.reason_code, latest.event_type);
      const gate = gatePhrase(latest.deciding_gate);
      if (why !== null && gate !== null) return `Could not dispatch to ${who}: ${why} (${gate}).`;
      if (why !== null) return `Could not dispatch to ${who}: ${why}.`;
      return `Could not dispatch to ${who}.`;
    }
  }
}

/**
 * Why a ready task is still waiting (dispatch.waiting): unavailable capacity (the Workers that
 * could do it are busy, or the one the owner named is offline or busy) is kept apart from
 * missing capability (no Worker's configured runtime can do it) and missing binding.
 */
function waitingLine(reason: string | null, who: string, required: unknown): string {
  const needs = Array.isArray(required) && required.length > 0
    ? ` (needs: ${required.filter((entry): entry is string => typeof entry === 'string').join(', ')})`
    : '';
  switch (reason) {
    case 'no_worker_free':
      return `Waiting for a free Worker: every Worker that can do this${needs} is busy.`;
    case 'no_capable_worker':
      return `Waiting: no Worker's configured runtime can do this${needs}. Brunel did not change any runtime.`;
    case 'no_bound_session':
      return 'Waiting: no Worker has a session bound to this project and workspace.';
    case 'pinned_worker_offline':
      return `Waiting for ${who}, which you named: it is offline. Brunel will not give it to another Worker.`;
    case 'pinned_worker_busy':
      return `Waiting for ${who}, which you named: it is busy with other work.`;
    case 'pinned_worker_unknown':
      return `Waiting for ${who}, which you named: no such Worker exists.`;
    default:
      return TASK_WAITING;
  }
}

/** True when the task's latest dispatch event says it waits only for a busy Worker. */
function isCapacityWait(taskId: string, audit: readonly AuditEvent[]): boolean {
  const latest = latestDispatchEvent(taskId, audit);
  return latest?.event_type === 'dispatch.waiting'
    && (latest.reason_code === 'no_worker_free' || latest.reason_code === 'pinned_worker_busy');
}

function detailOf(run: Pick<FinishedRun, 'summary' | 'output_tail'>): string | null {
  const summary = plainTerminalText(run.summary);
  if (summary !== '') return summary;
  const tail = plainTerminalText(run.output_tail ?? '');
  return tail === '' ? null : tail;
}

function finishedLine(run: FinishedRun, workers: readonly Worker[]): string {
  const how = run.success ? 'Done' : 'Failed';
  return `${how} in ${durationPhrase(runDurationMs(run))} · ${workerPhrase(run, workers)} · ${toolPhrase(run)}`;
}

function runningLine(run: RunningTask): string {
  const who = run.worker_number === null ? run.node_name : `Worker ${run.worker_number}`;
  return `Still going · ${who} on ${run.node_name}`;
}

/** The newest finished run of one task, or null when none of them is its. */
function newestRunFor(taskId: string, runs: readonly FinishedRun[]): FinishedRun | null {
  let best: FinishedRun | null = null;
  for (const run of runs) {
    if (run.task_id !== taskId) continue;
    if (best === null || Date.parse(run.reported_at) >= Date.parse(best.reported_at)) best = run;
  }
  return best;
}

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The headline: one line that is true whatever mix of states the tasks are in.
 * It leads with the failure when there is one, because that is the sentence the
 * owner needs before any other.
 */
function headlineFor(tasks: readonly TaskOutcome[]): string {
  const done = tasks.filter((t) => t.state === 'succeeded').length;
  const failed = tasks.filter((t) => t.state === 'failed').length;
  const going = tasks.filter((t) => t.state === 'running').length;
  const waiting = tasks.filter((t) => t.state === 'waiting').length;
  const settled = going === 0 && waiting === 0;

  if (settled) {
    if (failed === 0) {
      return tasks.length === 1 ? 'Done. The task finished.' : `Done. All ${count(tasks.length, 'task', 'tasks')} finished.`;
    }
    if (done === 0) {
      return tasks.length === 1 ? 'Failed. The task did not finish.' : `Failed. All ${count(tasks.length, 'task', 'tasks')} failed.`;
    }
    return `Finished with ${count(failed, 'task', 'tasks')} failed of ${tasks.length}.`;
  }

  // #644: a task waiting with a known dispatch blocker is blocked, and a task
  // no run holds is waiting. Only a run makes anything "under way".
  const blocked = tasks.filter((t) => t.state === 'waiting' && t.line !== TASK_WAITING && t.capacityWait !== true).length;
  const parts: string[] = [];
  if (done > 0) parts.push(`${done} done`);
  if (failed > 0) parts.push(`${failed} failed`);
  if (going > 0) parts.push(`${count(going, 'task is', 'tasks are')} still going`);
  if (blocked > 0) parts.push(`${blocked} blocked`);
  if (waiting - blocked > 0) parts.push(`${waiting - blocked} waiting to start`);
  if (going > 0) return `Under way: ${parts.join(', ')}.`;
  if (done === 0 && failed === 0) {
    if (blocked > 0) return `Accepted, not started: ${parts.join(', ')}.`;
    return tasks.length === 1
      ? 'Accepted, waiting to start - no Worker has picked it up.'
      : `Accepted, all ${count(tasks.length, 'task', 'tasks')} waiting to start - no Worker has picked them up.`;
  }
  return `Nothing running now: ${parts.join(', ')}.`;
}

/**
 * What became of one plan, or null when the plan is not one that runs - only an
 * approved plan has an outcome, and a proposed one is still a decision.
 */
export function planOutcome(
  plan: Plan,
  missionTasks: readonly MissionTask[],
  runs: readonly FinishedRun[],
  running: readonly RunningTask[],
  workers: readonly Worker[],
  audit: readonly AuditEvent[] = [],
  recoveries: readonly WaitingJobRecoveryRow[] = [],
): PlanOutcome | null {
  if (plan.status !== 'approved') return null;

  const missionId = plan.mission_id;
  const mine = missionId === null
    ? []
    : missionTasks.filter((t) => t.mission_id === missionId && t.project_id === plan.project_id);
  if (mine.length === 0) {
    return { headline: OUTCOME_NOT_MATCHED, settled: false, failed: false, tasks: [] };
  }

  // The plan's own order is the order the owner read them in, so the outcome
  // keeps it. A task the plan does not name goes after the ones it does.
  const planned = plan.tasks.map((t) => t.title);
  const ordered = [...mine].sort((a, b) => {
    const ai = planned.indexOf(a.title);
    const bi = planned.indexOf(b.title);
    return (ai === -1 ? planned.length : ai) - (bi === -1 ? planned.length : bi);
  });

  const tasks: TaskOutcome[] = ordered.map((task) => {
    const finished = newestRunFor(task.id, runs);
    if (finished !== null) {
      return {
        taskId: task.id,
        title: task.title,
        state: finished.success ? 'succeeded' : 'failed',
        line: finishedLine(finished, workers),
        detail: detailOf(finished),
      };
    }
    const live = running.find((r) => r.task_id === task.id);
    if (live !== undefined) {
      return { taskId: task.id, title: task.title, state: 'running', line: runningLine(live), detail: null };
    }
    const closed = closedWithoutRun(task, recoveries);
    if (closed !== null) return closed;
    const blocker = dispatchBlockerLine(task.id, audit, workers);
    return {
      taskId: task.id,
      title: task.title,
      state: 'waiting',
      line: blocker ?? TASK_WAITING,
      detail: null,
      ...(blocker !== null && isCapacityWait(task.id, audit) ? { capacityWait: true } : {}),
    };
  });

  return {
    headline: headlineFor(tasks),
    settled: tasks.every((t) => t.state === 'succeeded' || t.state === 'failed'),
    failed: tasks.some((t) => t.state === 'failed'),
    tasks,
  };
}

/** What became of every plan in view, by plan id, for the transcript to carry. */
export function planOutcomes(
  plans: readonly Plan[],
  missionTasks: readonly MissionTask[],
  runs: readonly FinishedRun[],
  running: readonly RunningTask[],
  workers: readonly Worker[],
  audit: readonly AuditEvent[] = [],
  recoveries: readonly WaitingJobRecoveryRow[] = [],
): Map<string, PlanOutcome> {
  const out = new Map<string, PlanOutcome>();
  for (const plan of plans) {
    const outcome = planOutcome(plan, missionTasks, runs, running, workers, audit, recoveries);
    if (outcome !== null) out.set(plan.id, outcome);
  }
  return out;
}
